import { Component, DestroyRef, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  YdsButtonComponent,
  YdsCardComponent,
  YdsDropdownComponent,
  YdsEmptyStatesComponent,
  YdsPaginationComponent,
  YdsSpinnerComponent,
  YdsSplitButtonComponent,
  YdsTableCellDirective,
  YdsTableComponent,
  type YdsDropdownOption,
  type YdsSplitButtonItem,
  type YdsTableColumn,
} from '../../../../yds-angular';
import { ImportRun, Institution, PayrollClient, PayrollRunService } from '../payroll-run.service';

/** Payload emitted when the primary action is triggered for a selected import run. */
export interface PayrollRunSelectionAction {
  importRunId: string;
  opdrachtgeverName: string;
  opdrachtgeverId: string | null;
  instellingId: string | null;
}

/** Payload emitted when the download list is requested for a selected import run. */
export interface PayrollRunSelectionDownloadList {
  opdrachtgeverId: string | null;
  instellingId: string | null;
  bookingYear: number | null;
  bookingPeriod: number | null;
}

@Component({
  selector: 'app-payroll-run-selection',
  imports: [
    YdsButtonComponent,
    YdsCardComponent,
    YdsDropdownComponent,
    YdsEmptyStatesComponent,
    YdsPaginationComponent,
    YdsSpinnerComponent,
    YdsSplitButtonComponent,
    YdsTableCellDirective,
    YdsTableComponent,
  ],
  templateUrl: './payroll-run-selection.component.html',
  styleUrl: './payroll-run-selection.component.scss',
})
export class PayrollRunSelectionComponent implements OnInit {
  readonly title = input.required<string>();
  readonly actionLabel = input.required<string>();
  readonly showFooterAction = input(true);
  readonly showActionColumn = input(true);
  readonly initialOpdrachtgeverId = input<string | null>(null);
  readonly initialInstellingId = input<string | null>(null);
  readonly action = output<PayrollRunSelectionAction>();
  readonly downloadList = output<PayrollRunSelectionDownloadList>();

  /** Initial selection to restore once on load; cleared after first use. */
  private pendingOpdrachtgeverId: string | null = null;
  private pendingInstellingId: string | null = null;

  readonly pageSize = 10;
  readonly tableColumns = computed<YdsTableColumn[]>(() => [
    { key: 'instelling', header: 'Instelling' },
    { key: 'periode', header: 'Periode' },
    { key: 'runnummer', header: 'Runnummer' },
    { key: 'soort', header: 'Soort' },
    ...(this.showActionColumn()
      ? [{ key: 'actions', header: 'Actieknop', width: '200px' } satisfies YdsTableColumn]
      : []),
  ]);

  readonly splitButtonItems = computed<readonly YdsSplitButtonItem[]>(() => [
    { id: 'action', label: this.actionLabel() },
    { id: 'downloadlist', label: 'Ga naar downloadlijst' },
  ]);

  private payrollRunService = inject(PayrollRunService);
  private destroyRef = inject(DestroyRef);

  payrollClients = signal<PayrollClient[]>([]);
  institutions = signal<Institution[]>([]);
  importRuns = signal<ImportRun[]>([]);

  opdrachtgeverId = signal<string | null>(null);
  instellingId = signal<string | null>(null);
  selectedRowId = signal<string | null>(null);

  isLoadingOpdrachtgevers = signal(true);
  isLoadingInstellingen = signal(false);
  isLoadingList = signal(false);

  pageIndex = signal(0);

  opdrachtgeverOptions = computed<YdsDropdownOption[]>(() =>
    this.payrollClients().map((client) => ({
      id: client.payrollClientId,
      label: client.name,
    })),
  );

  instellingOptions = computed<YdsDropdownOption[]>(() =>
    this.institutions().map((institution) => ({
      id: institution.institutionId,
      label: institution.name,
    })),
  );

  hasSelection = computed(() => this.selectedRowId() !== null);

  isLoadingAny = computed(
    () => this.isLoadingOpdrachtgevers() || this.isLoadingInstellingen() || this.isLoadingList(),
  );

  pagedImportRuns = computed(() => {
    const start = this.pageIndex() * this.pageSize;
    return this.importRuns().slice(start, start + this.pageSize);
  });

  tableItems = computed(() =>
    this.pagedImportRuns().map((run) => ({
      id: run.importRunId,
      instelling: run.institutionName,
      periode: `${run.year}/${String(run.period).padStart(2, '0')}`,
      runnummer: run.payrollRunNumber,
      soort: 'Vervanging',
    })),
  );

  tableSelectedIds = computed<readonly unknown[]>(() => {
    const selectedId = this.selectedRowId();
    return selectedId ? [selectedId] : [];
  });

  ngOnInit(): void {
    this.pendingOpdrachtgeverId = this.initialOpdrachtgeverId();
    this.pendingInstellingId = this.initialInstellingId();
    this.loadPayrollClients();
  }

  loadPayrollClients(): void {
    this.isLoadingOpdrachtgevers.set(true);
    this.payrollRunService
      .getPayrollClients()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (clients) => {
          this.payrollClients.set(clients);
          this.isLoadingOpdrachtgevers.set(false);
          const initialId = this.pendingOpdrachtgeverId;
          this.pendingOpdrachtgeverId = null;
          if (clients.length > 0) {
            const initial = clients.find((client) => client.payrollClientId === initialId);
            if (!initial) {
              this.pendingInstellingId = null;
            }
            this.onOpdrachtgeverChange((initial ?? clients[0]).payrollClientId);
          } else {
            this.onOpdrachtgeverChange(null);
          }
        },
        error: () => {
          this.payrollClients.set([]);
          this.isLoadingOpdrachtgevers.set(false);
        },
      });
  }

  onOpdrachtgeverChange(id: string | null): void {
    this.opdrachtgeverId.set(id);
    this.institutions.set([]);
    this.onInstellingChange(null);

    const client = this.payrollClients().find((item) => item.payrollClientId === id);
    if (client) {
      this.loadInstitutions(client.ceanr);
    }
  }

  loadInstitutions(ceaNr: number): void {
    this.isLoadingInstellingen.set(true);
    this.payrollRunService
      .getInstitutions(ceaNr)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (institutions) => {
          this.institutions.set(institutions);
          this.isLoadingInstellingen.set(false);
          const initialId = this.pendingInstellingId;
          this.pendingInstellingId = null;
          if (institutions.length > 0) {
            const initial = institutions.find((item) => item.institutionId === initialId);
            this.onInstellingChange((initial ?? institutions[0]).institutionId);
          }
        },
        error: () => {
          this.institutions.set([]);
          this.isLoadingInstellingen.set(false);
        },
      });
  }

  onInstellingChange(id: string | null): void {
    this.instellingId.set(id);
    this.importRuns.set([]);
    this.selectedRowId.set(null);
    this.pageIndex.set(0);

    const institution = this.institutions().find((item) => item.institutionId === id);
    if (institution) {
      this.loadImportRuns(institution.code);
    }
  }

  loadImportRuns(code: number): void {
    this.isLoadingList.set(true);
    this.payrollRunService
      .getImportRuns(code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (importRuns) => {
          this.importRuns.set(importRuns);
          this.isLoadingList.set(false);
          this.pageIndex.set(0);
          this.selectedRowId.set(null);
        },
        error: () => {
          this.importRuns.set([]);
          this.isLoadingList.set(false);
        },
      });
  }

  onTableRowClick(row: Record<string, unknown>): void {
    this.selectedRowId.set(String(row['id']));
    this.onActionClick();
  }

  onTableRowDoubleClick(row: Record<string, unknown>): void {
    this.selectedRowId.set(String(row['id']));
    this.onActionClick();
  }

  onRowActionClick(rowId: unknown, event?: MouseEvent): void {
    event?.stopPropagation();
    this.selectedRowId.set(String(rowId));
    this.onActionClick();
  }

  onRowMenuItemClick(rowId: unknown, actionId: string): void {
    if (actionId === 'action') {
      this.onRowActionClick(rowId);
      return;
    }

    if (actionId === 'downloadlist') {
      this.selectedRowId.set(String(rowId));

      const run = this.importRuns().find((item) => item.importRunId === String(rowId));
      this.downloadList.emit({
        opdrachtgeverId: this.opdrachtgeverId(),
        instellingId: run?.institutionId ?? this.instellingId(),
        bookingYear: run?.year ?? null,
        bookingPeriod: run?.period ?? null,
      });
    }
  }

  onActionClick(): void {
    const importRunId = this.selectedRowId();
    if (!importRunId) {
      return;
    }

    const opdrachtgeverName = this.payrollClients().find(
      (client) => client.payrollClientId === this.opdrachtgeverId(),
    )?.name;

    this.action.emit({
      importRunId,
      opdrachtgeverName: opdrachtgeverName ?? '',
      opdrachtgeverId: this.opdrachtgeverId(),
      instellingId: this.instellingId(),
    });
  }
}
